// Pieces the consent page, Settings and the quote and customer screens share for
// AI and app access (mock M8): the app's tile, the request box, and the badge and
// callout that name which app made a record and for whom.
import React from "react";
import type { Quote, Via } from "@phoneware/lens-domain";
import { Icon } from "../icons.js";
import { whenIn } from "../format.js";

/** The plug tile an app stands behind: blue while it works, grey when it is revoked or its person is turned off. */
export function AppTile({ off }: { off?: boolean }): React.ReactElement {
  return (
    <span className={off ? "apptile off" : "apptile"} aria-hidden="true">
      <Icon name="plug" size={16} />
    </span>
  );
}

/** The app asking or acting, with one line under its name. */
export function AppReq({ client, line }: { client: string; line: React.ReactNode }): React.ReactElement {
  return (
    <div className="req">
      <AppTile />
      <span className="rq">
        <b>{client}</b>
        <span>{line}</span>
      </span>
    </div>
  );
}

/** The app that made a record, when an app made it: Omni's own screens leave none. */
export function madeByApp(via: Via | null | undefined): Extract<Via, { kind: "api" | "mcp" }> | null {
  return via && via.kind !== "web" ? via : null;
}

/** "Created by Claude for Marisol Quintero", on a record an app made; nothing on one made in Omni. `block` sets it on its own line under a name. */
export function MadeBy({ via, person, block, testId }: { via: Via | null | undefined; person: string | null; block?: boolean; testId?: string }): React.ReactElement | null {
  const app = madeByApp(via);
  if (!app) return null;
  const badge = (
    <span className="made-by" title={`Through Omni\u2019s ${app.kind === "mcp" ? "MCP" : "API"}`} data-testid={testId}>
      <Icon name="plug" size={12} />
      <span>
        Created by {app.clientName}
        {person ? ` for ${person}` : ""}
      </span>
    </span>
  );
  return block ? <span className="made-by-cell">{badge}</span> : badge;
}

/** Above a draft an app made, until it is sent: nothing has gone to the customer, and sending it is the person's step. */
export function MadeByCallout({ quote, person }: { quote: Quote; person: string }): React.ReactElement | null {
  const app = madeByApp(quote.createdVia);
  if (!app || (quote.status !== "draft" && quote.status !== "needs_approval")) return null;
  return (
    <div className="made-by-callout" data-testid="quote-made-by-callout">
      <Icon name="plug" size={16} />
      <span>
        <b>
          {app.clientName} made this draft through Omni&rsquo;s {app.kind === "mcp" ? "MCP" : "API"} {whenIn(quote.createdAt)}, working as {person}.
        </b>{" "}
        Nothing has gone to the customer. Check it, then send the QuoteLink yourself: {app.clientName} can&rsquo;t send it.
      </span>
    </div>
  );
}
